import api from './index'

export interface EvReserveStats {
  total_reserves: number
  total_amount: number
  total_frozen_amount: number
  total_actual_amount: number
  total_freeze_amount: number
  total_charge_amount: number
  total_refund_amount: number
  freeze_transactions_count: number
  charge_transactions_count: number
  refund_transactions_count: number
  payments_count: number
  transactions_count: number
}

export interface EvReservesAllSummary {
  total_reserves: number
  total_trips: number
  total_combined: number
}

export interface EvReserveItemUser {
  id: string
  first_name: string
  last_name: string
  email: string
}

export interface EvReserveItemCarProfile {
  id: string
  brand: string
  model: string
  license_plate: string
}

export interface EvReserveItem {
  id: string
  type: 'reserve' | 'trip'
  status: string
  ev_car_profile_id: string
  user_id?: string
  renter_id?: string
  total_amount: number
  created_at: string
  start_time: string
  end_time: string
  payments: unknown[]
  transactions: unknown[]
  user?: EvReserveItemUser
  renter?: EvReserveItemUser
  car_profile?: {
    id: string
    model_make_id: string
    model_name: string
    plate_number: string
  }
  ev_car_profile?: {
    id: string
    model_make_id: string
    model_name: string
    plate_number: string
  }
}

export interface EvReservesAllPagination {
  current_page: number
  per_page: number
  total: number
  total_pages: number
  has_more: boolean
  has_prev: boolean
}

export interface EvReservesAllResponse {
  items: EvReserveItem[]
  pagination: EvReservesAllPagination
  summary: EvReservesAllSummary
}

export interface GetEvReservesAllParams {
  page?: number
  limit?: number
  status?: string
  car_profile_id?: string
  user_id?: string
  type?: 'reserve' | 'trip' | 'all'
  include_trips?: string
  count_only?: boolean | string
}

export interface OutstandingTrip {
  trip_id: string
  total_amount: number
  paid_amount: number
  outstanding_amount: number
  status: string
  created_at: string
  end_date: string
}

export interface OutstandingUser {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
  outstanding_trips: OutstandingTrip[]
  total_outstanding: number
}

export interface OutstandingPagination {
  current_page: number
  per_page: number
  total: number
  total_pages: number
  has_more: boolean
  has_prev: boolean
}

export interface OutstandingUsersResponse {
  users: OutstandingUser[]
  pagination: OutstandingPagination
}

export interface OutstandingStats {
  total_users_with_outstanding: number
  total_trips_with_outstanding: number
  total_outstanding_amount: number
  breakdown_by_status: Array<{
    status: string
    count: number
    total_outstanding: number
  }>
}

// API functions
export const getCarReserves = async (evCarProfileId: string) => {
  return api.get(`/ev-reserves/car/${evCarProfileId}/reserves`)
}

export const getUserReserves = async (userId: string) => {
  return api.get(`/ev-reserves/user/${userId}/reserves`)
}

export const getReserveDetails = async (reserveId: string) => {
  return api.get(`/ev-reserves/reserve/${reserveId}`)
}

export const getCarTransactions = async (
  evCarProfileId: string,
  params?: { page?: number; limit?: number }
) => {
  return api.get(`/ev-reserves/car/${evCarProfileId}/transactions`, { params })
}

export const getCarPayments = async (evCarProfileId: string) => {
  return api.get(`/ev-reserves/car/${evCarProfileId}/payments`)
}

export const getCarReserveStats = async (evCarProfileId: string) => {
  return api.get(`/ev-reserves/car/${evCarProfileId}/stats`)
}

export const getEvActiveReserves = async (params?: {
  page?: number
  limit?: number
}) => {
  return api.get<{ data: EvReservesAllResponse }>('/ev-reserves/all', {
    params: {
      page: params?.page ?? 1,
      limit: params?.limit ?? 1,
      type: 'reserve',
      status: 'reserved',
      include_trips: 'false',
    },
  })
}

export const getEvReservesAll = async (params: GetEvReservesAllParams = {}) => {
  return api.get<{ data: EvReservesAllResponse }>('/ev-reserves/all', {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      ...(params.status && { status: params.status }),
      ...(params.type && { type: params.type }),
      ...(params.car_profile_id && { car_profile_id: params.car_profile_id }),
      ...(params.user_id && { user_id: params.user_id }),
      ...(params.include_trips !== undefined && {
        include_trips: params.include_trips,
      }),
      ...(params.count_only !== undefined && {
        count_only: params.count_only,
      }),
    },
  })
}

export const getOutstandingUsers = async (params?: {
  page?: number
  limit?: number
  min_outstanding_amount?: number
}) => {
  return api.get<{ data: OutstandingUsersResponse }>(
    '/users-debt/outstanding',
    {
      params: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        min_outstanding_amount: params?.min_outstanding_amount ?? 0,
      },
    }
  )
}

export const getOutstandingStats = async () => {
  return api.get<{ data: OutstandingStats }>('/users-debt/stats')
}
